import {doc, setDoc, getDoc, Timestamp} from 'firebase/firestore';
import {db} from '../firebase';

/**
 * Scribe preferences persistence — mirrors iOS ScribePreferencesManager.
 *
 * Local layer:  localStorage (fast, synchronous reads for prompt hot path)
 * Remote layer: Firestore User/{userId}.scribePreferences (cross-device sync)
 *
 * Call syncFromFirebase() on login, syncToFirebase() on save,
 * clearLocalCache() on sign-out.
 */

const FIRESTORE_FIELD = 'scribePreferences';
const LS_PREFIX = 'scribe_pref_';

// Allowed values for the per-user "note length" / content-volume preference.
// Mirrors the backend's `ScribeNoteLength` type in
// calliope-backend/src/services/scribe/types.ts — keep these in lockstep.
// Anything outside this set is coerced back to 'standard' on read so a
// corrupted localStorage entry (or a remote value written by a newer client
// than we know about) can never break note generation.
const NOTE_LENGTH_VALUES = new Set(['detailed', 'standard', 'shortened']);
const DEFAULT_NOTE_LENGTH = 'standard';

// Valid EHR chart-history section ids the push modal can pre-select.
// Mirrors `EHR_HISTORY_TYPE_IDS` in PostSessionNotesView (and
// `HISTORY_TYPES` in calliope-backend/src/types/ehr.ts) — keep these in
// lockstep. Unknown ids are dropped on read/write so a stale or
// newer-client value can never feed garbage into the push payload.
const HISTORY_TYPE_VALUES = new Set([
  'medical',
  'surgical',
  'social',
  'family',
  'hospitalization',
]);

class ScribePreferencesService {
  // ── Custom instructions ──────────────────────────────────────

  getCustomInstructions() {
    return localStorage.getItem(`${LS_PREFIX}custom_instructions`) || '';
  }

  setCustomInstructions(value: any) {
    localStorage.setItem(`${LS_PREFIX}custom_instructions`, value || '');
  }

  // ── "Remember my instructions" toggle ────────────────────────
  //
  // Whether the free-text style instructions are persisted (and reused on
  // every future note) or applied to the current note only. Defaults to
  // `true` so existing accounts that already saved instructions keep
  // reusing them. When the doctor turns this OFF, the modal clears the
  // remembered value and routes the text through the ephemeral channel
  // below instead.

  getRememberInstructions() {
    // Default true: absence of the key (legacy accounts) means "remember",
    // which matches the historical always-persist behavior.
    return (
      localStorage.getItem(`${LS_PREFIX}remember_instructions`) !== 'false'
    );
  }

  setRememberInstructions(value: any) {
    localStorage.setItem(
      `${LS_PREFIX}remember_instructions`,
      String(Boolean(value)),
    );
  }

  // ── Ephemeral (this-note-only) instructions ──────────────────
  //
  // One-off style instructions that apply to the current note only and are
  // never persisted. Stored in sessionStorage (not localStorage) so they
  // survive a regenerate of the same note within the tab session but never
  // sync to Firestore or leak across browser sessions. Deliberately
  // excluded from `syncToFirebase` for the same reason `microphoneId` is —
  // it is intentionally device/session-scoped and not a durable preference.

  getEphemeralInstructions() {
    try {
      return sessionStorage.getItem(`${LS_PREFIX}ephemeral_instructions`) || '';
    } catch {
      return '';
    }
  }

  setEphemeralInstructions(value: any) {
    try {
      if (value) {
        sessionStorage.setItem(`${LS_PREFIX}ephemeral_instructions`, value);
      } else {
        sessionStorage.removeItem(`${LS_PREFIX}ephemeral_instructions`);
      }
    } catch {
      // sessionStorage can throw in private-mode / quota edge cases; a
      // failed write just means the one-off instruction isn't applied,
      // which is a safe no-op rather than a crash on the prompt hot path.
    }
  }

  clearEphemeralInstructions() {
    try {
      sessionStorage.removeItem(`${LS_PREFIX}ephemeral_instructions`);
    } catch {
      // no-op (see setEphemeralInstructions)
    }
  }

  // ── Capture microphone ───────────────────────────────────────
  //
  // The provider's preferred input device. Stored in localStorage ONLY — a
  // MediaDevices `deviceId` is scoped to this browser/device and is meaningless
  // (and misleading) on another machine, so it is deliberately excluded from
  // the Firestore cross-device sync below.

  getMicrophoneId() {
    return localStorage.getItem(`${LS_PREFIX}microphone_id`) || '';
  }

  setMicrophoneId(deviceId: string) {
    if (deviceId) localStorage.setItem(`${LS_PREFIX}microphone_id`, deviceId);
    else localStorage.removeItem(`${LS_PREFIX}microphone_id`);
  }

  // ── Formatting preferences ───────────────────────────────────

  getFormatting() {
    return {
      useQuotes: localStorage.getItem(`${LS_PREFIX}use_quotes`) === 'true',
      useAbbreviations:
        localStorage.getItem(`${LS_PREFIX}use_abbreviations`) !== 'false',
      useBullets: localStorage.getItem(`${LS_PREFIX}use_bullets`) !== 'false',
      // Off until the provider opts in: writes the note in the plain,
      // everyday language spoken during the visit instead of medical
      // shorthand. Defaults false so existing accounts are unchanged.
      plainLanguage:
        localStorage.getItem(`${LS_PREFIX}plain_language`) === 'true',
    };
  }

  setFormatting({
    useQuotes,
    useAbbreviations,
    useBullets,
    plainLanguage,
  }: Record<string, any>) {
    if (useQuotes !== undefined)
      localStorage.setItem(`${LS_PREFIX}use_quotes`, String(useQuotes));
    if (useAbbreviations !== undefined)
      localStorage.setItem(
        `${LS_PREFIX}use_abbreviations`,
        String(useAbbreviations),
      );
    if (useBullets !== undefined)
      localStorage.setItem(`${LS_PREFIX}use_bullets`, String(useBullets));
    if (plainLanguage !== undefined)
      localStorage.setItem(`${LS_PREFIX}plain_language`, String(plainLanguage));
  }

  // ── Note length / content volume ─────────────────────────────
  //
  // Single per-user preference that applies to the entire generated note
  // (not per-template). Stored as a small string so we don't have to
  // version a numeric scale.

  getNoteLength() {
    const raw = localStorage.getItem(`${LS_PREFIX}note_length`);
    return raw && NOTE_LENGTH_VALUES.has(raw) ? raw : DEFAULT_NOTE_LENGTH;
  }

  setNoteLength(value: any) {
    if (!NOTE_LENGTH_VALUES.has(value)) return;
    localStorage.setItem(`${LS_PREFIX}note_length`, value);
  }

  // ── Per-section note length overrides ────────────────────────
  //
  // Optional per-section verbosity overrides layered on top of the note-wide
  // `noteLength`. Keyed templateId → sectionName → length so a provider can
  // keep (say) a shortened HPI alongside a detailed Plan in the same
  // template. A section absent from the map inherits the global `noteLength`.
  // The backend resolves `sectionNoteLengths[name] ?? noteLength` per section.

  getSectionNoteLengthsMap() {
    try {
      const parsed = JSON.parse(
        localStorage.getItem(`${LS_PREFIX}section_note_lengths`) || '{}',
      );
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  // Returns { [sectionName]: length } for a single template, coercing away any
  // stray values so a corrupted entry can never feed an invalid length into
  // note generation.
  getSectionNoteLengths(templateId: any) {
    if (!templateId) return {};
    const forTemplate = this.getSectionNoteLengthsMap()[templateId];
    if (!forTemplate || typeof forTemplate !== 'object') return {};
    const clean: Record<string, any> = {};
    Object.entries(forTemplate).forEach(([section, value]) => {
      if (NOTE_LENGTH_VALUES.has(value as string)) clean[section] = value;
    });
    return clean;
  }

  getSectionNoteLength(templateId: any, sectionName: any) {
    return this.getSectionNoteLengths(templateId)[sectionName] || null;
  }

  setSectionNoteLength(templateId: any, sectionName: any, value: any) {
    if (!templateId || !sectionName) return;
    if (!NOTE_LENGTH_VALUES.has(value)) return;
    const map = this.getSectionNoteLengthsMap();
    const forTemplate = {...(map[templateId] || {})};
    forTemplate[sectionName] = value;
    map[templateId] = forTemplate;
    localStorage.setItem(
      `${LS_PREFIX}section_note_lengths`,
      JSON.stringify(map),
    );
  }

  // Drops a section's override so it falls back to the global `noteLength`.
  clearSectionNoteLength(templateId: any, sectionName: any) {
    if (!templateId || !sectionName) return;
    const map = this.getSectionNoteLengthsMap();
    const forTemplate = map[templateId];
    if (!forTemplate || !(sectionName in forTemplate)) return;
    delete forTemplate[sectionName];
    if (Object.keys(forTemplate).length === 0) delete map[templateId];
    else map[templateId] = forTemplate;
    localStorage.setItem(
      `${LS_PREFIX}section_note_lengths`,
      JSON.stringify(map),
    );
  }

  // ── EHR history-import selection ─────────────────────────────
  //
  // Which chart-history sections the EHR push modal pre-selects, stored
  // as an array of valid `HISTORY_TYPE_VALUES` ids so the provider's
  // last choice is remembered across sessions and devices.
  //
  // `null` (no stored entry) means "never chosen" — the EHR push modal
  // treats that as its product default (import all sections). An explicit
  // empty array is a real, remembered "off" (import nothing) choice and
  // round-trips as `[]` so it survives reopen and cross-device sync.

  getHistoryImportSelection() {
    const raw = localStorage.getItem(`${LS_PREFIX}history_import_types`);
    if (raw == null) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return parsed.filter(id => HISTORY_TYPE_VALUES.has(id));
    } catch {
      return null;
    }
  }

  setHistoryImportSelection(ids: any) {
    if (!Array.isArray(ids)) return;
    const clean = ids.filter(id => HISTORY_TYPE_VALUES.has(id));
    localStorage.setItem(
      `${LS_PREFIX}history_import_types`,
      JSON.stringify(clean),
    );
  }

  // ── Firebase sync ────────────────────────────────────────────

  async syncToFirebase(userId: any) {
    if (!userId) return;
    const data = {
      customInstructions: this.getCustomInstructions(),
      rememberInstructions: this.getRememberInstructions(),
      formatting: this.getFormatting(),
      noteLength: this.getNoteLength(),
      sectionNoteLengths: this.getSectionNoteLengthsMap(),
      // Omit when never chosen so we don't overwrite a value written by
      // another device with `null`. An explicit empty array (remembered
      // "off") is preserved.
      historyImportTypes: this.getHistoryImportSelection() ?? undefined,
      lastUpdated: Timestamp.now(),
    };
    if (data.historyImportTypes === undefined) delete data.historyImportTypes;

    try {
      await setDoc(
        doc(db, 'User', userId),
        {[FIRESTORE_FIELD]: data},
        {merge: true},
      );
    } catch (err) {
      console.error('ScribePreferences: Firestore write failed:', err);
    }
  }

  async syncFromFirebase(userId: any) {
    if (!userId) return;
    try {
      const snap = await getDoc(doc(db, 'User', userId));
      if (!snap.exists()) {
        this.clearLocalCache();
        return;
      }

      const prefs = snap.data()?.[FIRESTORE_FIELD];
      if (!prefs) {
        this.clearLocalCache();
        return;
      }

      if (prefs.customInstructions != null) {
        this.setCustomInstructions(prefs.customInstructions);
      }
      if (prefs.rememberInstructions != null) {
        this.setRememberInstructions(prefs.rememberInstructions);
      }
      if (prefs.formatting) {
        this.setFormatting(prefs.formatting);
      }
      if (prefs.noteLength) {
        this.setNoteLength(prefs.noteLength);
      }
      if (
        prefs.sectionNoteLengths &&
        typeof prefs.sectionNoteLengths === 'object'
      ) {
        localStorage.setItem(
          `${LS_PREFIX}section_note_lengths`,
          JSON.stringify(prefs.sectionNoteLengths),
        );
      }
      if (Array.isArray(prefs.historyImportTypes)) {
        this.setHistoryImportSelection(prefs.historyImportTypes);
      }
    } catch (err) {
      console.error('ScribePreferences: Firestore read failed:', err);
    }
  }

  // ── Cache management ─────────────────────────────────────────

  clearLocalCache() {
    [
      'custom_instructions',
      'remember_instructions',
      'use_quotes',
      'use_abbreviations',
      'use_bullets',
      'plain_language',
      'note_length',
      'section_note_lengths',
      'history_import_types',
      'microphone_id',
    ].forEach(k => localStorage.removeItem(`${LS_PREFIX}${k}`));
    // The one-off ephemeral instruction lives in sessionStorage; drop it too
    // on sign-out so it never leaks into the next account's notes.
    this.clearEphemeralInstructions();
  }
}

export const scribePreferences = new ScribePreferencesService();
